import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Filter, Heart, Shield, Clock, Award, CheckCircle, Star,
  TrendingUp, Users, Phone, ChevronRight, AlertCircle, Zap, X,
  HelpCircle, MessageCircle, Beaker, Package, ShoppingCart, Activity,
  FileText, Download, Calendar, Home as HomeIcon, Microscope, ArrowLeft,
  Droplet, Brain, Bone, Eye, Stethoscope, Pill, Syringe, TestTube,
  HeartPulse, Dna, Thermometer, Glasses
} from 'lucide-react';
import api from '../../../api/axios';
import { AuthContext } from '../../../context/AuthContext';
import { useCart } from '../../../context/CartContext';
import Header from '../../common/Header';
import Footer from '../../common/Footer';

const MedicalTestMain = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const { cart, addMedicalTest, addMedicalPackage, removeItem, isAuthenticated } = useCart();

  // Array of different medical icons for variety
  const medicalIcons = [
    Droplet, Brain, Heart, Microscope, TestTube,
    Syringe, HeartPulse, Dna, Activity, Thermometer,
    Stethoscope, Eye, Bone, Pill, Beaker, Glasses
  ];

  // Get icon based on test ID for consistency
  const getTestIcon = (id) => {
    const iconIndex = id % medicalIcons.length;
    return medicalIcons[iconIndex];
  };

  // Core state
  const [activeTab, setActiveTab] = useState('tests');
  const [tests, setTests] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Selection state
  const [selectedTests, setSelectedTests] = useState([]);
  const [selectedPackages, setSelectedPackages] = useState([]);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('popular');
  const [filteredItems, setFilteredItems] = useState([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    total_pages: 0,
    has_next: false,
    has_previous: false
  });

  // Load selections from localStorage
  useEffect(() => {
    try {
      const storedTests = localStorage.getItem('selectedTests');
      const storedPackages = localStorage.getItem('selectedPackages');

      if (storedTests && storedTests !== 'undefined') {
        const parsedTests = JSON.parse(storedTests);
        if (Array.isArray(parsedTests)) setSelectedTests(parsedTests);
      }

      if (storedPackages && storedPackages !== 'undefined') {
        const parsedPackages = JSON.parse(storedPackages);
        if (Array.isArray(parsedPackages)) setSelectedPackages(parsedPackages);
      }
    } catch (err) {
      console.error('Error loading stored selections:', err);
      localStorage.removeItem('selectedTests');
      localStorage.removeItem('selectedPackages');
    }
  }, []);

  // Fetch data
  useEffect(() => {
    Promise.all([fetchTests(), fetchPackages()]);
  }, [currentPage]);

  const fetchTests = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/medical-tests/?page=${currentPage}&limit=20`);
      const testData = response.data.data || response.data;
      setTests(Array.isArray(testData) ? testData : []);

      // Set pagination data
      if (response.data.pagination) {
        setPagination(response.data.pagination);
      }

      setError(null);
    } catch (err) {
      setError('Failed to fetch tests');
      console.error('Error fetching tests:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPackages = async () => {
    try {
      const response = await api.get('/medical-test-packages/');
      const pkgData = response.data.data || response.data;
      setPackages(Array.isArray(pkgData) ? pkgData : []);
    } catch (err) {
      console.error('Error fetching packages:', err);
      setPackages([]);
    }
  };

  // Filter and sort logic
  useEffect(() => {
    let items = activeTab === 'tests' ? [...tests] : [...packages];

    // Search filter
    if (searchQuery) {
      items = items.filter(item => {
        const title = activeTab === 'tests' ? item.title : item.name;
        const desc = item.description || '';
        return (
          title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          desc.toLowerCase().includes(searchQuery.toLowerCase())
        );
      });
    }

    // Sort
    if (sortBy === 'price-low') {
      items.sort((a, b) => {
        const priceA = parseFloat(a.offer_price || a.effective_price || a.price || 0);
        const priceB = parseFloat(b.offer_price || b.effective_price || b.price || 0);
        return priceA - priceB;
      });
    } else if (sortBy === 'price-high') {
      items.sort((a, b) => {
        const priceA = parseFloat(a.offer_price || a.effective_price || a.price || 0);
        const priceB = parseFloat(b.offer_price || b.effective_price || b.price || 0);
        return priceB - priceA;
      });
    } else if (sortBy === 'discount') {
      items.sort((a, b) => {
        const discountA = a.discount_percentage || 0;
        const discountB = b.discount_percentage || 0;
        return discountB - discountA;
      });
    }

    setFilteredItems(items);
  }, [searchQuery, sortBy, tests, packages, activeTab]);

  const handleSelectTest = async (test) => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    try {
      // Check if test is already in cart
      const cartItem = cart?.test_items?.find(item => item.test?.id === test.id);

      if (cartItem) {
        // Remove from cart via API
        await removeItem('test', cartItem.id);

        // Also update local state for UI
        setSelectedTests(prev => {
          const newSelection = prev.filter(t => t.id !== test.id);
          localStorage.setItem('selectedTests', JSON.stringify(newSelection));
          return newSelection;
        });
      } else {
        // Add test to cart via API
        await addMedicalTest(test.id, 1);

        // Also update local state for UI
        setSelectedTests(prev => {
          const newSelection = [...prev, test];
          localStorage.setItem('selectedTests', JSON.stringify(newSelection));
          return newSelection;
        });
      }
    } catch (error) {
      console.error('Error updating cart:', error);
      alert(error.message || 'Failed to update cart');
    }
  };

  const handleSelectPackage = async (pkg) => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    try {
      // Check if package is already in cart
      const cartItem = cart?.test_items?.find(item => item.package?.id === pkg.id);

      if (cartItem) {
        // Remove from cart via API
        await removeItem('test', cartItem.id);

        // Also update local state for UI
        setSelectedPackages(prev => {
          const newSelection = prev.filter(p => p.id !== pkg.id);
          localStorage.setItem('selectedPackages', JSON.stringify(newSelection));
          return newSelection;
        });
      } else {
        // Add package to cart via API
        await addMedicalPackage(pkg.id, 1);

        // Also update local state for UI
        setSelectedPackages(prev => {
          const newSelection = [...prev, pkg];
          localStorage.setItem('selectedPackages', JSON.stringify(newSelection));
          return newSelection;
        });
      }
    } catch (error) {
      console.error('Error updating cart:', error);
      alert(error.message || 'Failed to update cart');
    }
  };

  const getTotalItems = () => selectedTests.length + selectedPackages.length;

  if (loading && tests.length === 0) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
          <div className="text-center">
            <div className="relative">
              <div className="animate-spin rounded-full h-20 w-20 border-4 border-indigo-200 border-t-indigo-600 mx-auto"></div>
              <Microscope className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 h-8 text-indigo-600 animate-pulse" />
            </div>
            <p className="mt-6 text-gray-700 text-lg font-medium">Loading medical tests...</p>
            <p className="mt-2 text-gray-500 text-sm">Preparing comprehensive health packages for you</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">

        {/* Back Button */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800 font-semibold transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Home
          </button>
        </div>

        {/* Hero Section */}
        <div className="relative bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute transform rotate-45 -left-20 -top-20 w-96 h-96 bg-white rounded-full"></div>
            <div className="absolute transform -rotate-12 right-0 top-40 w-72 h-72 bg-white rounded-full"></div>
            <div className="absolute transform rotate-180 left-1/3 bottom-0 w-64 h-64 bg-white rounded-full"></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center max-w-4xl mx-auto">
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                <Shield className="w-4 h-4" />
                <span className="text-sm font-medium">NABL Accredited Laboratory</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 leading-tight">
                Complete Health Checkups
                <span className="block text-indigo-200">From Home</span>
              </h1>

              <p className="text-lg md:text-xl text-indigo-100 mb-8 max-w-2xl mx-auto">
                Premium diagnostic services with free home sample collection.
                Accurate results delivered in 24-48 hours.
              </p>

              {/* Search Bar */}
              <div className="max-w-2xl mx-auto mb-8">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Search for tests, packages, health checkups..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-14 py-4 rounded-2xl text-gray-900 shadow-2xl focus:outline-none focus:ring-4 focus:ring-indigo-300 text-lg"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 p-2 rounded-full bg-gray-200 hover:bg-gray-300 text-gray-600 hover:text-gray-900 transition-all duration-200 flex items-center justify-center"
                      style={{ background: 'rgb(229, 231, 235)', width: 'auto', border: 'none' }}
                      aria-label="Clear search"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-4 md:gap-8 max-w-3xl mx-auto">
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-bold mb-1">1000+</div>
                  <div className="text-sm md:text-base text-indigo-100">Tests Available</div>
                </div>
                <div className="text-center border-x border-white/30">
                  <div className="text-3xl md:text-4xl font-bold mb-1">4.9★</div>
                  <div className="text-sm md:text-base text-indigo-100">Patient Rating</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-bold mb-1">24-48h</div>
                  <div className="text-sm md:text-base text-indigo-100">Results</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs & Filter Bar */}
        <div className="bg-white border-b sticky top-0 z-10 shadow-sm">
          <div className="max-w-[1600px] mx-auto px-3 sm:px-4 py-2 sm:py-3">
            {/* Mobile Layout */}
            <div className="flex md:hidden items-center justify-between gap-2">
              {/* Tabs - Compact */}
              <div className="flex gap-1.5">
                <button
                  onClick={() => setActiveTab('tests')}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    activeTab === 'tests'
                      ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  <Beaker className="w-3 h-3" />
                  Tests
                </button>
                <button
                  onClick={() => setActiveTab('packages')}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    activeTab === 'packages'
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  <Package className="w-3 h-3" />
                  Packages
                </button>
              </div>

              {/* Cart Button Only - Compact */}
              <button
                onClick={() => navigate('/medical-tests/booking')}
                disabled={getTotalItems() === 0}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-base font-semibold transition-all ${
                  getTotalItems() > 0
                    ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                <ShoppingCart className="w-3 h-3" />
                <span className="hidden xs:inline">Cart</span> ({getTotalItems()})
              </button>
            </div>

            {/* Desktop Layout */}
            <div className="hidden md:flex items-center justify-between gap-4">
              {/* Tabs */}
              <div className="flex gap-2">
                <button
                  onClick={() => setActiveTab('tests')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold transition-all text-sm ${
                    activeTab === 'tests'
                      ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Beaker className="w-4 h-4" />
                  Individual Tests
                </button>
                <button
                  onClick={() => setActiveTab('packages')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold transition-all text-sm ${
                    activeTab === 'packages'
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Package className="w-4 h-4" />
                  Health Packages
                </button>
              </div>

              {/* Filter & Cart */}
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-700">
                  {filteredItems.length} {activeTab === 'tests' ? 'Test' : 'Package'}{filteredItems.length !== 1 ? 's' : ''}
                </span>
                {searchQuery && (
                  <span className="text-base bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full">
                    for "{searchQuery}"
                  </span>
                )}
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white text-sm"
                >
                  <option value="popular">Most Popular</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="discount">Best Discount</option>
                </select>
                <button
                  onClick={() => navigate('/medical-tests/booking')}
                  disabled={getTotalItems() === 0}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold transition-all text-sm ${
                    getTotalItems() > 0
                      ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700 shadow-lg'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <ShoppingCart className="w-4 h-4" />
                  Cart ({getTotalItems()})
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-[1600px] mx-auto px-3 sm:px-4 lg:px-6 py-8">
          {filteredItems.length === 0 ? (
            <div className="text-center py-20">
              <div className="bg-white rounded-2xl shadow-xl p-12 max-w-md mx-auto">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Search className="w-12 h-12 text-gray-400" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-3">No Results Found</h3>
                <p className="text-gray-600 mb-6">
                  {searchQuery
                    ? `No results for "${searchQuery}". Try different keywords.`
                    : `No ${activeTab} available at the moment.`
                  }
                </p>
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    Clear Search
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {filteredItems.map((item) => {
                const isTest = activeTab === 'tests';

                // Check if item is in backend cart
                const isInCart = cart && cart.test_items ? cart.test_items.some(cartItem => {
                  if (isTest) {
                    return cartItem.test?.id === item.id;
                  } else {
                    return cartItem.package?.id === item.id;
                  }
                }) : false;

                // Check local selection state OR backend cart
                const isSelected = isTest
                  ? (selectedTests.some(t => t.id === item.id) || isInCart)
                  : (selectedPackages.some(p => p.id === item.id) || isInCart);

                const discount = item.discount_percentage || 0;
                const price = parseFloat(item.offer_price || item.effective_price || item.price || 0);
                const originalPrice = parseFloat(item.price || item.total_price || 0);

                // Get dynamic icon for tests
                const TestIcon = isTest ? getTestIcon(item.id) : Package;

                return (
                  <div
                    key={item.id}
                    className={`group bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden border ${
                      isSelected ? 'border-indigo-500 ring-1 ring-indigo-200' : 'border-gray-200 hover:border-indigo-300'
                    } flex flex-col h-full`}
                  >
                    {/* Compact Header */}
                    <div className={`relative p-3 ${isTest ? 'bg-gradient-to-br from-indigo-50 to-purple-50' : 'bg-gradient-to-br from-purple-50 to-pink-50'}`}>
                      {discount > 0 && (
                        <div className="absolute top-1.5 right-1.5 bg-red-500 text-white px-1.5 py-0.5 rounded text-base font-bold">
                          {Math.round(discount)}%
                        </div>
                      )}

                      <div className="flex items-start gap-2">
                        <div className={`w-12 h-12 ${isTest ? 'bg-indigo-100' : 'bg-purple-100'} rounded-md flex items-center justify-center flex-shrink-0`}>
                          <TestIcon className={`w-7 h-7 ${isTest ? 'text-indigo-600' : 'text-purple-600'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm md:text-lg font-bold text-gray-900 line-clamp-2 leading-tight">
                            {isTest ? item.title : item.name}
                          </h3>
                          {!isTest && item.test_count > 0 && (
                            <span className="inline-block mt-1 text-xs text-purple-600 font-medium">
                              {item.test_count} Tests
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Content Area */}
                    <div className="flex-1 flex flex-col p-3 pt-2.5">
                      {/* Description */}
                      <p className="text-xs md:text-base text-gray-600 leading-relaxed mb-3 line-clamp-3 min-h-[3.5rem]">
                        {(item.description || 'Professional diagnostic test with accurate results.')}
                      </p>

                      {/* Features - Compact Inline */}
                      <div className="flex items-center gap-1.5 mb-3 text-xs text-gray-600">
                        <span className="flex items-center gap-0.5">
                          <CheckCircle className="w-3 h-3 text-green-500" />
                          Free
                        </span>
                        <span className="flex items-center gap-0.5">
                          <Clock className="w-3 h-3 text-blue-500" />
                          24-48h
                        </span>
                        <span className="flex items-center gap-0.5">
                          <Shield className="w-3 h-3 text-indigo-500" />
                          NABL
                        </span>
                      </div>

                      {/* Pricing & Actions */}
                      <div className="mt-auto pt-3 border-t border-gray-100">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <div className="flex items-baseline gap-1">
                              <span className="text-sm md:text-base font-bold text-indigo-600">
                                ₹{price}
                              </span>
                              {discount > 0 && (
                                <span className="text-xs md:text-base text-gray-400 line-through">
                                  ₹{originalPrice}
                                </span>
                              )}
                            </div>
                            {discount > 0 && (
                              <p className="text-xs md:text-base text-green-600 font-medium">
                                Save ₹{(originalPrice - price).toFixed(0)}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Action Buttons */}
                        {isTest ? (
                          <div className="flex gap-2">
                            <button
                              onClick={() => navigate(`/medical-test/${item.id}`)}
                              className="flex-1 py-2.5 px-3 rounded-md text-xs md:text-base font-semibold transition-all border border-indigo-600 text-indigo-600 hover:bg-indigo-50"
                            >
                              View
                            </button>
                            <button
                              onClick={() => handleSelectTest(item)}
                              className={`flex-1 py-2.5 px-3 rounded-md text-xs md:text-base font-semibold transition-all ${
                                isSelected
                                  ? 'bg-green-600 text-white hover:bg-green-700'
                                  : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700'
                              }`}
                            >
                              {isSelected ? (
                                <span className="flex items-center justify-center gap-1">
                                  <CheckCircle className="w-3 h-3" />
                                  Added
                                </span>
                              ) : (
                                <span className="flex items-center justify-center gap-1">
                                  <ShoppingCart className="w-3 h-3" />
                                  Add
                                </span>
                              )}
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => navigate(`/test-package/${item.id}`)}
                            className="w-full py-2.5 px-3 rounded-md text-xs md:text-base font-semibold transition-all bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 flex items-center justify-center gap-1"
                          >
                            <span>View Package</span>
                            <ChevronRight className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination - Only for Tests Tab */}
          {activeTab === 'tests' && !searchQuery && pagination.total_pages > 1 && (
            <div className="mt-12 flex justify-center items-center gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={!pagination.has_previous}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  pagination.has_previous
                    ? 'bg-white text-indigo-600 border-2 border-indigo-600 hover:bg-indigo-50'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                <ChevronRight className="w-5 h-5 rotate-180" />
              </button>

              <div className="flex items-center gap-2">
                {[...Array(pagination.total_pages)].map((_, index) => {
                  const pageNum = index + 1;
                  // Show first page, last page, current page, and pages around current
                  if (
                    pageNum === 1 ||
                    pageNum === pagination.total_pages ||
                    (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`w-10 h-10 rounded-lg font-semibold transition-all ${
                          currentPage === pageNum
                            ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg'
                            : 'bg-white text-gray-700 border border-gray-300 hover:border-indigo-600 hover:text-indigo-600'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                    return <span key={pageNum} className="text-gray-400">...</span>;
                  }
                  return null;
                })}
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(pagination.total_pages, prev + 1))}
                disabled={!pagination.has_next}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  pagination.has_next
                    ? 'bg-white text-indigo-600 border-2 border-indigo-600 hover:bg-indigo-50'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                <ChevronRight className="w-5 h-5" />
              </button>

              <div className="ml-4 text-sm text-gray-600 bg-white px-4 py-2 rounded-lg border">
                Page {currentPage} of {pagination.total_pages} ({pagination.total} tests)
              </div>
            </div>
          )}
        </div>

        {/* Why Choose Us Section */}
        <div className="bg-white border-y">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Why Choose Our Lab Services?
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Trusted by thousands for accurate diagnostics and timely reports
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center group">
                <div className="w-20 h-20 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Shield className="w-10 h-10 text-indigo-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2 text-lg">NABL Accredited</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Certified laboratory with international quality standards
                </p>
              </div>

              <div className="text-center group">
                <div className="w-20 h-20 bg-gradient-to-br from-green-100 to-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <HomeIcon className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2 text-lg">Free Home Collection</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Professional phlebotomist visits your home at your convenience
                </p>
              </div>

              <div className="text-center group">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Clock className="w-10 h-10 text-blue-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2 text-lg">Quick Reports</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Get digital reports within 24-48 hours via email and SMS
                </p>
              </div>

              <div className="text-center group">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Award className="w-10 h-10 text-purple-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2 text-lg">Best Pricing</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Competitive prices with regular discounts and package deals
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* How It Works Section */}
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                How It Works
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Get your health checkup done in 4 simple steps
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[
                { num: '1', icon: <Search className="w-8 h-8" />, title: 'Choose Tests', desc: 'Browse and select tests or packages' },
                { num: '2', icon: <Calendar className="w-8 h-8" />, title: 'Book Slot', desc: 'Schedule convenient time for sample collection' },
                { num: '3', icon: <HomeIcon className="w-8 h-8" />, title: 'Sample Collection', desc: 'Phlebotomist visits your home' },
                { num: '4', icon: <FileText className="w-8 h-8" />, title: 'Get Report', desc: 'Receive digital report in 24-48 hours' }
              ].map((step, idx) => (
                <div key={idx} className="relative">
                  <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                    <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-500 text-white rounded-2xl flex items-center justify-center text-2xl font-bold mb-4 shadow-lg">
                      {step.num}
                    </div>
                    <div className="text-indigo-600 mb-3">{step.icon}</div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{step.desc}</p>
                  </div>
                  {idx < 3 && (
                    <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                      <ChevronRight className="w-6 h-6 text-indigo-300" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Emergency Contact */}
        <div className="bg-gradient-to-r from-red-600 to-pink-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-center md:text-left">
                <div className="flex items-center gap-3 justify-center md:justify-start mb-2">
                  <Phone className="w-6 h-6 animate-pulse" />
                  <h3 className="text-2xl font-bold">Need Urgent Lab Test?</h3>
                </div>
                <p className="text-red-100">Our team is available 24/7 for emergency sample collection</p>
              </div>
              <a
                href="tel:+919876543210"
                className="bg-white text-red-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-red-50 transition-colors shadow-2xl flex items-center gap-2 group"
              >
                <Phone className="w-5 h-5 group-hover:animate-bounce" />
                <span>Call: +91 98765 43210</span>
              </a>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-4 py-2 rounded-full mb-4">
                <HelpCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Got Questions?</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Everything you need to know about our lab services
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              {/* Support Card */}
              <div className="relative order-2 lg:order-1">
                <div className="sticky top-24">
                  <div className="relative bg-gradient-to-br from-indigo-100 to-purple-100 rounded-3xl p-8 shadow-2xl overflow-hidden">
                    <div className="relative z-10 bg-white rounded-2xl p-8 shadow-xl">
                      <div className="flex flex-col items-center justify-center text-center space-y-6">
                        <div className="flex items-end gap-4 mb-4">
                          <div className="w-24 h-24 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center shadow-lg">
                            <Microscope className="w-12 h-12 text-white" />
                          </div>
                        </div>

                        <div className="space-y-3">
                          <h3 className="text-2xl font-bold text-gray-900">Need Assistance?</h3>
                          <p className="text-gray-600 leading-relaxed">
                            Our healthcare experts are ready to help you choose the right tests
                          </p>
                        </div>

                        <div className="w-full space-y-3 pt-4">
                          <a
                            href="tel:+919876543210"
                            className="w-full flex items-center justify-center gap-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg"
                          >
                            <Phone className="w-5 h-5" />
                            <span>Call: +91 98765 43210</span>
                          </a>
                          <a
                            href="https://wa.me/919876543210?text=Hi%2C%20I%20need%20help%20with%20lab%20tests"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full flex items-center justify-center gap-3 bg-white border-2 border-indigo-600 text-indigo-600 px-6 py-3 rounded-xl font-semibold hover:bg-indigo-50 transition-all"
                          >
                            <MessageCircle className="w-5 h-5" />
                            <span>WhatsApp Chat</span>
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* FAQs */}
              <div className="space-y-4 order-1 lg:order-2">
                {[
                  {
                    q: "Is home sample collection free?",
                    a: "Yes, we provide free home sample collection for all tests and packages. Our trained phlebotomist will visit your home at your scheduled time."
                  },
                  {
                    q: "How long does it take to get reports?",
                    a: "Most test reports are delivered within 24-48 hours. You'll receive them via email and SMS. Critical test reports may be available sooner."
                  },
                  {
                    q: "Are your labs NABL accredited?",
                    a: "Yes, all our partner laboratories are NABL accredited and maintain the highest quality standards for accurate and reliable results."
                  },
                  {
                    q: "Can I book tests for family members?",
                    a: "Absolutely! You can book multiple tests for different family members in a single appointment. Our phlebotomist will collect all samples during one visit."
                  },
                  {
                    q: "What if I need to reschedule?",
                    a: "You can reschedule your appointment up to 2 hours before the scheduled time without any charges. Contact our support team for assistance."
                  },
                  {
                    q: "How do I access my reports?",
                    a: "Reports are sent via email and SMS with a secure link. You can also download them from your account dashboard anytime."
                  }
                ].map((faq, index) => (
                  <details key={index} className="group bg-gray-50 rounded-2xl overflow-hidden shadow-md hover:shadow-lg transition-shadow">
                    <summary className="flex items-start justify-between cursor-pointer p-6 hover:bg-gray-100 transition-colors">
                      <div className="flex items-start gap-3 flex-1">
                        <div className="mt-1 flex-shrink-0">
                          <HelpCircle className="w-5 h-5 text-indigo-600" />
                        </div>
                        <span className="font-semibold text-gray-900 text-left pr-4">{faq.q}</span>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400 group-open:rotate-90 transition-transform flex-shrink-0 mt-1" />
                    </summary>
                    <div className="px-6 pb-6 pl-14 text-gray-600 leading-relaxed">
                      {faq.a}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </div>

      </div>
      <Footer />
    </>
  );
};

export default MedicalTestMain;
